import { useEffect, useMemo, useState } from "react";
import { api, SIZES } from "@/lib/api";
import { Printer, Copy } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function SoleOrder() {
  const [rows, setRows] = useState(null);
  const [supplier, setSupplier] = useState("");
  const [note, setNote] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/soles/shortage").then((r) => setRows(r.data)).catch(() => setFailed(true));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((r) => {
      const k = `${r.sole_code}||${r.sole_color}`;
      if (!map.has(k)) map.set(k, { key: k, code: r.sole_code, color: r.sole_color, models: r.models, sizes: {}, total: 0 });
      const g = map.get(k);
      g.sizes[r.size] = r.missing;
      g.total += r.missing;
      if (r.models.length) g.models = r.models;
    });
    return [...map.values()];
  }, [rows]);

  const grand = grouped.reduce((a, g) => a + g.total, 0);

  const copyText = async () => {
    const lines = [`TABAN SİPARİŞİ${supplier ? " - " + supplier : ""}`, `Tarih: ${new Date().toLocaleDateString("tr-TR")}`, ""];
    grouped.forEach((g) => {
      const sizes = Object.entries(g.sizes).map(([s, q]) => `${s}: ${q}`).join(", ");
      lines.push(`${g.code}${g.color ? " " + g.color : ""} → ${sizes} (Toplam ${g.total} çift)`);
    });
    lines.push("", `GENEL TOPLAM: ${grand} çift`);
    if (note) lines.push(`Not: ${note}`);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Liste kopyalandı — WhatsApp veya e-postaya yapıştırabilirsiniz");
    } catch { toast.error("Kopyalanamadı"); }
  };

  return (
    <div className="min-h-screen bg-white text-black" data-testid="sole-order-page">
      <div className="max-w-[1000px] mx-auto p-8 sm:p-12">
        <div className="print:hidden mb-6 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
          <label className="text-xs font-mono uppercase tracking-widest text-gray-500">Tedarikçi
            <input data-testid="sole-order-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)}
              placeholder="Örn: Kaya Taban" className="mt-1 w-full h-11 border border-gray-300 rounded-md px-3 text-black normal-case tracking-normal text-sm" />
          </label>
          <label className="text-xs font-mono uppercase tracking-widest text-gray-500">Not
            <input data-testid="sole-order-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Opsiyonel" className="mt-1 w-full h-11 border border-gray-300 rounded-md px-3 text-black normal-case tracking-normal text-sm" />
          </label>
          <button onClick={copyText} disabled={!grouped.length} data-testid="sole-order-copy-btn"
            className="flex items-center justify-center gap-2 border border-[#D97706] text-[#B45309] hover:bg-orange-50 px-5 h-11 rounded-md font-display uppercase tracking-wide disabled:opacity-40">
            <Copy className="w-4 h-4" /> Metni Kopyala
          </button>
          <button onClick={() => window.print()} disabled={!grouped.length} data-testid="sole-order-print-btn"
            className="flex items-center justify-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white px-5 h-11 rounded-md font-display uppercase tracking-wide disabled:opacity-40">
            <Printer className="w-4 h-4" /> Yazdır
          </button>
        </div>

        <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
          <div>
            <Logo dark className="text-4xl" subtitle="Ayakkabı Üretim" testId="print-logo" />
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-4" data-testid="print-title">Taban Sipariş Listesi</h1>
          </div>
          <div className="text-right text-sm font-mono">
            <p>Tarih: {new Date().toLocaleDateString("tr-TR")}</p>
            {supplier && <p>Tedarikçi: <b data-testid="sole-order-supplier-print">{supplier}</b></p>}
          </div>
        </div>

        {failed && <p className="text-red-600" data-testid="sole-order-error">Liste yüklenemedi. Sayfayı yenileyin.</p>}
        {rows === null && !failed && <p className="text-gray-500">Yükleniyor...</p>}
        {rows && grouped.length === 0 && (
          <p className="text-green-700 font-semibold" data-testid="sole-order-empty">
            Açık siparişler için eksik taban yok — depodaki tabanlar yeterli.
          </p>
        )}
        {grouped.length > 0 && (
          <table className="w-full text-sm border border-black" data-testid="sole-order-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-black px-2 py-1.5 text-left">Taban Kodu</th>
                <th className="border border-black px-2 py-1.5 text-left">Renk</th>
                {SIZES.map((s) => <th key={s} className="border border-black px-1 py-1.5 text-center w-10">{s}</th>)}
                <th className="border border-black px-2 py-1.5 text-right">Toplam</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <tr key={g.key} data-testid={`sole-order-row-${g.key}`}>
                  <td className="border border-black px-2 py-1.5 font-mono font-semibold">
                    {g.code}
                    {g.models.length > 0 && <div className="text-[10px] font-normal text-gray-500">Model: {g.models.join(", ")}</div>}
                  </td>
                  <td className="border border-black px-2 py-1.5">{g.color || "—"}</td>
                  {SIZES.map((s) => (
                    <td key={s} className="border border-black px-1 py-1.5 text-center font-mono" data-testid={`sole-order-qty-${g.key}-${s}`}>
                      {g.sizes[s] || ""}
                    </td>
                  ))}
                  <td className="border border-black px-2 py-1.5 text-right font-mono font-bold">{g.total}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td colSpan={12} className="border border-black px-2 py-1.5 text-right">GENEL TOPLAM</td>
                <td className="border border-black px-2 py-1.5 text-right font-mono" data-testid="sole-order-total">{grand}</td>
              </tr>
            </tbody>
          </table>
        )}
        {note && <p className="text-sm mt-4">Not: {note}</p>}
        <p className="text-[11px] text-gray-500 mt-6 print:hidden">
          Hesap: açık siparişlerin kalan adedinden hazır ayakkabı stoğu çıkarılır; kalan üretim için gereken taban, depodaki tabandan fazlaysa farkı listelenir.
          Daha önce tabansız yapılan kesimler (eksi taban stoğu) da eklenir.
        </p>
      </div>
    </div>
  );
}
